import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cx } from './cx';

export function Menu({ trigger, children, align = 'end', className }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const menuId = useId();

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) close(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    menuRef.current?.querySelector('[role="menuitem"], [role="menuitemradio"]')?.focus();
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const onMenuKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = [...menuRef.current.querySelectorAll('[role="menuitem"], [role="menuitemradio"]')];
    const index = items.indexOf(document.activeElement);
    const next = items[(index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
    next?.focus();
  };

  return (
    <div ref={rootRef} className={cx('menu-anchor', className)}>
      {trigger({
        ref: triggerRef,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
        onClick: () => setOpen((value) => !value),
      })}
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          className={cx('menu', align === 'end' ? 'menu--end' : 'menu--start')}
          onKeyDown={onMenuKeyDown}
          onClick={(event) => {
            if (event.target.closest('[role="menuitem"], [role="menuitemradio"]')) close(false);
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
